import { Component, effect, inject, input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ApiResult } from 'src/app/models/ApiResult';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { GetInternalWorkerDto } from 'src/app/models/dtos/get-internal-worker-dto';
import { FirstNameValidator } from 'src/app/shared/validators/first-name.validator';
import { LastNameValidator } from 'src/app/shared/validators/last-name.validator';

@Component({
  selector: 'app-internal-worker-form',
  templateUrl: './internal-worker-form.component.html',
  styleUrls: ['./internal-worker-form.component.scss'],
  standalone: false
})
export class InternalWorkerFormComponent {
  private formBuilder = inject(UntypedFormBuilder);

  readonly id = input<string | null>(null);
  readonly internalWorkerResponse = input<ApiResult<GetInternalWorkerDto> | null>(null, { alias: 'internalWorker' });

  internalWorkerId = 0;
  internalWorker: GetInternalWorkerDto | null = null;
  firstTabFormGroup!: UntypedFormGroup;
  secondTabFormGroup!: UntypedFormGroup;

  constructor() {
    effect(() => {
      this.internalWorkerId = +(this.id() ?? 0);
      this.internalWorker = this.internalWorkerResponse()?.Value ?? null;
      this.onInternalWorkerRetrieved();
      this.setEmailAndPhoneDisabled();
    });
  }

  get internalWorkerOrganization() {
    return this.internalWorker?.InternalWorkerEmployerId
      ? ({ Id: this.internalWorker?.InternalWorkerEmployerId, Name: this.internalWorker?.InternalWorkerEmployer } as DictionaryItem)
      : null;
  }
  get internalWorkerSupervisor() {
    return this.internalWorker?.SupervisorId
      ? ({ Id: this.internalWorker?.SupervisorId, Name: this.internalWorker?.Supervisor } as DictionaryItem)
      : null;
  }

  onInternalWorkerRetrieved() {
    if (this.firstTabFormGroup == null) {
      this.firstTabFormGroup = this.formBuilder.group({
        firstName: [, [Validators.required, Validators.maxLength(50), FirstNameValidator()]],
        lastName: [null, [Validators.required, Validators.maxLength(50), LastNameValidator()]],
        email: [
          { value: null, disabled: this.internalWorkerId !== 0 },
          {
            validators: [Validators.required, Validators.maxLength(50), Validators.email],
            updateOn: 'blur',
          },
        ],
        phone: [
          { value: null, disabled: this.internalWorkerId !== 0 },
          {
            validators: [Validators.required, Validators.pattern('^[0-9]{4,12}$')],
            updateOn: 'blur',
          },
        ],
        sendWelcomeMessage: [{ value: true, disabled: true }],
        roleId: [null, [Validators.required]],
        organization: [null],
        supervisor: [null],
        isEmployee: [{ value: null, disabled: true }]
      });
    }

    this.firstTabFormGroup.patchValue(
      {
        firstName: this.internalWorker?.FirstName,
        lastName: this.internalWorker?.LastName,
        email: this.internalWorker?.Email,
        phone: this.internalWorker?.PhoneNumber,
        roleId: this.internalWorker?.Role?.Id,
        organization: this.internalWorkerOrganization,
        supervisor: this.internalWorkerSupervisor,
        isEmployee: this.internalWorker?.IsEmployee
      },
      { onlySelf: true, emitEvent: false },
    );

    const organizationControl = this.firstTabFormGroup.get('organization');
    const supervisorControl = this.firstTabFormGroup.get('supervisor');

    if (this.internalWorker?.Role?.IsOrganizationRequired) {
      organizationControl?.setValidators([Validators.required]);
    } else {
      organizationControl?.clearValidators();
    }
    organizationControl?.updateValueAndValidity({ emitEvent: false });

    if (this.internalWorker?.Role?.IsSupervisorRequired) {
      supervisorControl?.setValidators([Validators.required]);
    } else {
      supervisorControl?.clearValidators();
    }
    supervisorControl?.updateValueAndValidity({ emitEvent: false });

    this.firstTabFormGroup.markAsUntouched();
    this.firstTabFormGroup.markAsPristine();
  }

  onChangeFirstTab(form: UntypedFormGroup): void {
    this.firstTabFormGroup = form;
  }

  onChangeSecondTab(form: UntypedFormGroup): void {
    this.secondTabFormGroup = form;
  }

  private setEmailAndPhoneDisabled() {
    if (this.internalWorkerId === 0) {
      this.firstTabFormGroup.get('email')?.enable();
      this.firstTabFormGroup.get('phone')?.enable();
    } else {
      this.firstTabFormGroup.get('email')?.disable();
      this.firstTabFormGroup.get('phone')?.disable();
    }
  }
}
